// src/context/ChatContext.js (Complete Updated Code with Trigger Function)
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types'; // Import PropTypes
import { useAuth } from './AuthContext'; // Keep this for auth status
import apiClient from '../api/apiClient'; // Adjust path if needed

// Key for storing the last active session ID in localStorage
const LAST_ACTIVE_SESSION_KEY = 'lastActiveSessionId';

// Create the context
export const ChatContext = createContext({
    triggerDataRefresh: () => {}, // Add this
});

// Provider component
// *** MODIFIED: Accept triggerDocumentUploadImplementation and triggerDataRefresh props ***
export const ChatProvider = ({ children, triggerDocumentUploadImplementation, triggerDataRefresh }) => {
    const { user, loading: authLoading } = useAuth();

    // Initial state for the chat context
    const [chatState, setChatState] = useState({
        sessionsList: [],
        isLoadingSessions: false,
        sessionsError: null,
        messages: [],
        sessionId: null,
        activeSessionId: null,
        isLoadingSession: true,
        sessionError: null,
    });

    // State to control the visibility of the inline cart summary
    const [showInlineCart, setShowInlineCart] = useState(false);

    // --- Callback to fetch the list of user's chat sessions ---
    const fetchSessions = useCallback(async () => {
        if (!user && !authLoading) { console.log("Ctx: Skip fetch, no user"); setChatState(prev => ({ ...prev, sessionsList: [], isLoadingSessions: false, sessionsError: null })); return []; }
        if (authLoading) { console.log("Ctx: Skip fetch, auth loading"); return []; }
        setChatState(prev => ({ ...prev, isLoadingSessions: true, sessionsError: null }));
        try {
            const response = await apiClient.get('/chat/sessions');
            const sessions = response.data || [];
            setChatState(prev => ({ ...prev, sessionsList: sessions, isLoadingSessions: false }));
            console.log("Ctx: Fetched sessions:", sessions.length);
            return sessions;
        } catch (error) {
            const errorMsg = error.response?.data?.error || `Failed fetch (${error.response?.status || 'Network'})`;
            console.error('Ctx: Error fetching sessions:', error.response || error);
            setChatState(prev => ({ ...prev, sessionsError: errorMsg, isLoadingSessions: false, sessionsList: [] }));
            throw error;
        }
    }, [user, authLoading]);

    // --- Callback to load the history for a specific chat session ---
    const loadSession = useCallback(async (sessionIdToLoad) => {
        if (!user && !authLoading) { /* ... */ return; }
        if (authLoading) { /* ... */ return; }
        if (sessionIdToLoad === null || sessionIdToLoad === undefined || isNaN(parseInt(sessionIdToLoad, 10))) { /* ... */ return; }

        const numericSessionId = parseInt(sessionIdToLoad, 10);
        console.log(`Ctx: Loading session: ${numericSessionId}`);
        setShowInlineCart(false);
        setChatState(prev => ({ ...prev, isLoadingSession: true, sessionError: null, messages: [], sessionId: numericSessionId, activeSessionId: null }));

        try {
            const response = await apiClient.get(`/chat/history/${numericSessionId}`);
            const historyMessages = response.data || [];
            if (!Array.isArray(historyMessages)) { throw new Error("Invalid history format"); }
            console.log(`Ctx: Loaded ${historyMessages.length} msgs for session ${numericSessionId}.`);
            setChatState(prev => ({ ...prev, messages: historyMessages, isLoadingSession: false, sessionError: null, sessionId: numericSessionId, activeSessionId: numericSessionId }));
        } catch (error) {
            const status = error.response?.status;
            let errorMsg = `Failed to load session ${numericSessionId}`;
            if (status === 404) errorMsg = "Session not found.";
            else if (status === 403) errorMsg = "Access denied.";
            else errorMsg = error.response?.data?.error || `Load failed (${status || 'Network'})`;
            console.error(`Ctx: Error loading session ${numericSessionId}:`, error.response || error);
            setChatState(prev => ({ ...prev, isLoadingSession: false, sessionError: errorMsg, activeSessionId: null, sessionId: null, messages: [] }));
            const lastStored = localStorage.getItem(LAST_ACTIVE_SESSION_KEY);
            if (lastStored === String(numericSessionId)) { localStorage.removeItem(LAST_ACTIVE_SESSION_KEY); }
            setShowInlineCart(false);
        }
    }, [user, authLoading]);

    // --- Callback to start a new chat session ---
    const startNewSession = useCallback(async () => {
         if (!user && !authLoading) { /* ... */ return; }
         if (authLoading) { /* ... */ return; }
        console.log("Ctx: Starting new session...");
        setShowInlineCart(false);
        setChatState(prev => ({ ...prev, isLoadingSession: true, sessionError: null, messages: [], sessionId: null, activeSessionId: null }));

        try {
            const response = await apiClient.post('/chat/new_session');
            const newSessionId = response.data?.session_id;
            if (newSessionId === null || newSessionId === undefined || isNaN(parseInt(newSessionId, 10))) { throw new Error("Invalid session_id from backend"); }
            const numericNewSessionId = parseInt(newSessionId, 10);
            console.log("Ctx: New session ID:", numericNewSessionId);
            setChatState(prev => ({ ...prev, sessionId: numericNewSessionId, activeSessionId: numericNewSessionId, messages: [], isLoadingSession: false, sessionError: null }));
            await fetchSessions(); // Refresh list
        } catch (error) {
            const errorMsg = error.response?.data?.error || `Failed create (${error.response?.status || 'Network'})`;
            console.error('Ctx: Error starting new session:', error.response || error);
            setChatState(prev => ({ ...prev, isLoadingSession: false, sessionError: errorMsg, sessionId: null, activeSessionId: null, messages: [] }));
            setShowInlineCart(false);
            localStorage.removeItem(LAST_ACTIVE_SESSION_KEY);
        }
    }, [user, authLoading, fetchSessions]);

    // --- Initialization Effect ---
    useEffect(() => {
        let mounted = true; // Add mounted flag for cleanup

        const initializeChat = async () => {
            console.log("Ctx: Init Effect. User:", !!user, "AuthLoading:", authLoading);
            if (authLoading || !mounted) return;
            
            if (!user) {
                setChatState({ 
                    sessionsList: [], 
                    isLoadingSessions: false, 
                    sessionsError: null, 
                    messages: [], 
                    sessionId: null, 
                    activeSessionId: null, 
                    isLoadingSession: false, 
                    sessionError: null 
                });
                setShowInlineCart(false);
                localStorage.removeItem(LAST_ACTIVE_SESSION_KEY);
                return;
            }

            try {
                console.log("Ctx: User authenticated. Initializing...");
                const fetchedSessions = await fetchSessions();
                if (!mounted) return;

                const lastSessionId = localStorage.getItem(LAST_ACTIVE_SESSION_KEY);
                const parsedId = lastSessionId ? parseInt(lastSessionId, 10) : null;
                const sessionExists = parsedId !== null && 
                    fetchedSessions.some(session => session.id === parsedId);

                if (sessionExists && parsedId !== null) {
                    console.log("Ctx: Loading last active session:", parsedId);
                    await loadSession(parsedId);
                } else {
                    if (parsedId !== null) {
                        console.log(`Ctx: Last session ${parsedId} invalid/not found.`);
                        localStorage.removeItem(LAST_ACTIVE_SESSION_KEY);
                    }
                    console.log("Ctx: Starting new session automatically.");
                    await startNewSession();
                }
            } catch (error) {
                if (!mounted) return;
                console.error("Ctx: Error during initialization:", error);
                setChatState(prev => ({
                    ...prev,
                    isLoadingSession: false,
                    isLoadingSessions: false,
                    sessionError: "Chat initialization failed",
                    sessionsError: "Failed to load sessions",
                    activeSessionId: null,
                    sessionId: null,
                    messages: [],
                    sessionsList: []
                }));
                localStorage.removeItem(LAST_ACTIVE_SESSION_KEY);
            }
        };

        initializeChat();

        return () => {
            mounted = false;
        };
    }, [user, authLoading]); // Only depend on user and authLoading

    // --- Effect to Save Last Active Session ID ---
    useEffect(() => {
        if (chatState.activeSessionId !== null && !isNaN(parseInt(chatState.activeSessionId, 10))) {
             const currentStoredId = localStorage.getItem(LAST_ACTIVE_SESSION_KEY);
             const newIdString = String(chatState.activeSessionId);
             if (currentStoredId !== newIdString) { localStorage.setItem(LAST_ACTIVE_SESSION_KEY, newIdString); console.log(`Ctx: Saved activeSessionId ${newIdString}.`); }
        }
    }, [chatState.activeSessionId]);


    // --- *** NEW: Function Definition for Triggering Upload UI *** ---
    // This uses the implementation passed down from DashboardWithChat
    const triggerDocumentUpload = useCallback((orderItemId) => {
        if (triggerDocumentUploadImplementation && typeof triggerDocumentUploadImplementation === 'function') {
            triggerDocumentUploadImplementation(orderItemId); // Call the actual handler
        } else {
            // This warning should ideally not appear if DashboardWithChat is set up correctly
            console.warn("ChatContext: triggerDocumentUpload called, but no implementation was provided by the Provider wrapper. Item ID:", orderItemId);
        }
    }, [triggerDocumentUploadImplementation]); // Dependency on the passed-in function


    // --- Context Value ---
    // Assemble the value to be provided by the context
    const contextValue = {
        chatState,
        setChatState, // Be mindful when exposing this directly
        startNewSession,
        loadSession,
        fetchSessions,
        showInlineCart,
        setShowInlineCart,
        triggerDocumentUpload, // *** ADD THE NEW FUNCTION TO THE VALUE ***
        triggerDataRefresh, // Add this
    };

    // Return the provider wrapping the children components
    return (
        <ChatContext.Provider value={contextValue}>
            {children}
        </ChatContext.Provider>
    );
};

// *** ADD PropTypes for the ChatProvider ***
ChatProvider.propTypes = {
    children: PropTypes.node.isRequired,
    triggerDocumentUploadImplementation: PropTypes.func, // Mark implementation as optional but expected func
    triggerDataRefresh: PropTypes.func // Add PropTypes for triggerDataRefresh
};


// Custom hook to easily consume the chat context (Unchanged)
export function useChat() {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}